<template>
  <div>
    <el-card>
      <template slot="header">
        <div class="card-header">
          <span>系统设置</span>
        </div>
      </template>
      <el-form :model="form" label-width="100px" style="max-width: 600px">
        <el-form-item label="系统名称">
          <el-input v-model="form.system_name" placeholder="请输入系统名称" maxlength="20" show-word-limit></el-input>
        </el-form-item>
        <el-form-item label="系统Logo">
          <div class="logo-area">
            <div class="logo-preview" v-if="form.system_logo">
              <img :src="getImageUrl(form.system_logo)" class="logo-img" />
              <div class="logo-actions">
                <el-button type="danger" size="mini" icon="el-icon-delete" circle @click="form.system_logo = ''"></el-button>
              </div>
            </div>
            <el-upload v-else action="#" :auto-upload="false" :show-file-list="false" :on-change="handleLogoUpload" accept="image/*" class="logo-uploader">
              <div class="logo-placeholder">
                <i class="el-icon-plus"></i>
                <p>上传Logo</p>
              </div>
            </el-upload>
          </div>
          <div class="logo-tip">建议尺寸：128x128px，支持 PNG/JPG 格式</div>
        </el-form-item>
        <el-form-item label="预览效果">
          <div class="preview-box">
            <div class="preview-sidebar">
              <div class="preview-logo-area">
                <img v-if="form.system_logo" :src="getImageUrl(form.system_logo)" class="preview-logo-img" />
                <i v-else class="el-icon-s-goods preview-logo-icon"></i>
                <span class="preview-logo-text">{{ form.system_name || '存货宝' }}</span>
              </div>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="小程序首页轮播图">
          <div class="banner-list">
            <div v-for="(url, idx) in banners" :key="idx" class="banner-item">
              <img :src="getImageUrl(url)" class="banner-img" />
              <div class="banner-actions">
                <el-button size="mini" icon="el-icon-top" circle :disabled="idx === 0" @click="moveBanner(idx, -1)" title="上移"></el-button>
                <el-button size="mini" icon="el-icon-bottom" circle :disabled="idx === banners.length - 1" @click="moveBanner(idx, 1)" title="下移"></el-button>
                <el-button type="danger" size="mini" icon="el-icon-delete" circle @click="removeBanner(idx)" title="删除"></el-button>
              </div>
            </div>
            <el-upload action="#" :auto-upload="false" :show-file-list="false" :on-change="handleBannerUpload" accept="image/*" class="banner-uploader">
              <div class="banner-placeholder">
                <i class="el-icon-plus"></i>
                <p>添加图片</p>
              </div>
            </el-upload>
          </div>
          <div class="logo-tip">建议尺寸：750x350px，支持 PNG/JPG；上下拖动序号控制展示顺序</div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSave" :loading="saving">保存设置</el-button>
          <el-button @click="loadConfigs">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card style="margin-top:16px">
      <template slot="header">
        <div class="card-header">
          <span>后台风格</span>
          <span style="font-size:12px;color:#909399">选择预设可一键应用，下方亦可微调</span>
        </div>
      </template>
      <el-row :gutter="16" class="preset-grid">
        <el-col :xs="12" :sm="8" :md="6" :lg="4" v-for="p in presets" :key="p.key">
          <div class="preset-card" :class="{ active: isActivePreset(p) }" @click="applyPreset(p)">
            <div class="preset-thumb" :style="{ background: p.sidebar }">
              <div class="preset-thumb-logo"></div>
              <div class="preset-thumb-item"></div>
              <div class="preset-thumb-item active-item" :style="{ background: p.primary }"></div>
              <div class="preset-thumb-item"></div>
            </div>
            <div class="preset-meta">
              <span class="preset-dot" :style="{ background: p.primary }"></span>
              <span class="preset-name">{{ p.name }}</span>
              <i v-if="isActivePreset(p)" class="el-icon-success preset-check"></i>
            </div>
            <div class="preset-desc">{{ p.desc }}</div>
          </div>
        </el-col>
      </el-row>

      <el-divider content-position="left">自定义微调</el-divider>
      <el-form :model="theme" label-width="120px" style="max-width:600px">
        <el-form-item label="主题色">
          <el-color-picker v-model="theme.primary"></el-color-picker>
          <span style="margin-left:12px;color:#909399;font-size:12px">用于侧边栏当前菜单高亮等</span>
        </el-form-item>
        <el-form-item label="侧边栏背景">
          <el-color-picker v-model="theme.sidebar"></el-color-picker>
        </el-form-item>
        <el-form-item label="紧凑模式">
          <el-switch v-model="theme.compact"></el-switch>
          <span style="margin-left:12px;color:#909399;font-size:12px">减小内边距/行高，适合 1080P 以下屏幕</span>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="saveTheme" :loading="themeSaving">保存风格</el-button>
          <el-button @click="resetTheme">恢复默认</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 业务规则配置 -->
    <el-card style="margin-top:16px">
      <template slot="header">
        <div class="card-header">
          <span>业务规则配置</span>
          <span style="font-size:12px;color:#909399">出库申请、签字等业务流程的限制规则</span>
        </div>
      </template>
      <el-form label-width="180px" style="max-width:800px">
        <el-form-item label="未签字明细上限">
          <el-input-number v-model="maxUnsignedLimit" :min="1" :max="99" :step="1" controls-position="right"></el-input-number>
          <div class="logo-tip" style="margin-top:8px">业务员名下未签字明细超过此数量时，禁止办理送存货、尊贵卡类型的出库申请（不包括新出库）</div>
        </el-form-item>
        <el-form-item label="新出库未完结订单上限">
          <el-input-number v-model="maxNewoutUnfinishedLimit" :min="1" :max="99" :step="1" controls-position="right"></el-input-number>
          <div class="logo-tip" style="margin-top:8px">业务员名下新出库未完结订单超过此数量时，禁止办理新出库类型的出库申请</div>
        </el-form-item>
        <el-form-item label="业务员逾期还款期数上限">
          <el-input-number v-model="maxCustomerDebtLimit" :min="1" :max="99" :step="1" controls-position="right"></el-input-number>
          <div class="logo-tip" style="margin-top:8px">业务员名下逾期还款计划超过此数量时，禁止创建新的有欠款的订单（用于控制风险和催款）</div>
        </el-form-item>
        <el-form-item label="客户年度金额上限（元）">
          <el-input-number v-model="maxCustomerYearAmount" :min="1000" :max="9999999" :step="1000" controls-position="right"></el-input-number>
          <div class="logo-tip" style="margin-top:8px">客户本年度已支付金额（明细总额-实时欠款）达到上限时，自动列入禁售名单。保存规则后自动重新计算所有客户的禁售状态。</div>
        </el-form-item>
        <el-form-item label="服务费休眠缓冲期">
          <el-input-number v-model="serviceFeeBufferValue" :min="0" :max="365" :step="1" controls-position="right" style="width:120px"></el-input-number>
          <el-select v-model="serviceFeeBufferUnit" style="width:100px;margin-left:10px">
            <el-option label="天" value="day"></el-option>
            <el-option label="月" value="month"></el-option>
          </el-select>
          <div class="logo-tip" style="margin-top:8px">尊贵卡B/C服务费到期后，不会立即休眠，而是在缓冲期后才设置为休眠状态。缓冲期内仍不可提货，但提示语会区分"即将休眠"和"已休眠"。</div>
        </el-form-item>
        <el-form-item label="尊贵卡A提货缓冲期">
          <el-input-number v-model="vipABufferMonths" :min="1" :max="120" :step="1" controls-position="right" style="width:140px"></el-input-number>
          <span class="logo-tip" style="margin-left: 10px;">月</span>
          <div class="logo-tip" style="margin-top:8px">尊贵卡A客户距上次提货时间（或合同起始日）超过该月数无提货时，自动进入休眠缓冲期。默认 12 个月。只要客户一直提货，永不休眠。</div>
        </el-form-item>
        <el-form-item label="尊贵卡A休眠月数">
          <el-input-number v-model="vipADormantMonths" :min="1" :max="120" :step="1" controls-position="right" style="width:140px"></el-input-number>
          <span class="logo-tip" style="margin-left: 10px;">月</span>
          <div class="logo-tip" style="margin-top:8px">进入休眠缓冲期后，若再超过该月数仍未提货，正式转为休眠状态。默认 12 个月（即累计 24 个月未提货休眠）。</div>
        </el-form-item>
        <el-form-item label="礼品休眠未领取月数">
          <el-input-number v-model="giftDormantMonths" :min="0" :max="120" :step="1" controls-position="right" style="width:140px"></el-input-number>
          <span class="logo-tip" style="margin-left: 10px;">月</span>
          <div class="logo-tip" style="margin-top:8px">礼品名单中客户若连续超过该月数无礼品申请动态时，自动转为礼品休眠状态；留空或填 0 表示不开启礼品休眠规则。</div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="saveBusinessRules" :loading="businessRulesSaving">保存规则</el-button>
          <el-button @click="resetBusinessRules">恢复默认</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 业务员解禁管理 -->
    <el-card style="margin-top:16px">
      <template slot="header">
        <div class="card-header">
          <span>业务员解禁管理</span>
          <span style="font-size:12px;color:#909399">当业务员达到上限被禁时，可手动解禁增加额外配额</span>
        </div>
      </template>
      <el-form label-width="180px" style="max-width:800px">
        <el-form-item label="未签字明细解禁">
          <el-select v-model="selectedUnsignedUser" placeholder="选择业务员" clearable style="width:200px;margin-right:10px">
            <el-option v-for="u in salesmen" :key="u.id" :label="u.name" :value="u.id"></el-option>
          </el-select>
          <el-button type="warning" @click="unlockBonus('unsigned')">解禁</el-button>
          <div class="logo-tip" style="margin-top:8px">解禁后，该业务员重新获得未签字明细上限次数的额外配额</div>
        </el-form-item>
        <el-form-item label="新出库未完结订单解禁">
          <el-select v-model="selectedNewoutUser" placeholder="选择业务员" clearable style="width:200px;margin-right:10px">
            <el-option v-for="u in salesmen" :key="u.id" :label="u.name" :value="u.id"></el-option>
          </el-select>
          <el-button type="warning" @click="unlockBonus('newout')">解禁</el-button>
          <div class="logo-tip" style="margin-top:8px">解禁后，该业务员重新获得新出库未完结订单上限次数的额外配额</div>
        </el-form-item>
        <el-form-item label="逾期还款期数解禁">
          <el-select v-model="selectedOverdueUser" placeholder="选择业务员" clearable style="width:200px;margin-right:10px">
            <el-option v-for="u in salesmen" :key="u.id" :label="u.name" :value="u.id"></el-option>
          </el-select>
          <el-button type="warning" @click="unlockBonus('overdue')">解禁</el-button>
          <div class="logo-tip" style="margin-top:8px">解禁后，该业务员重新获得逾期还款期数上限次数的额外配额</div>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 小程序业务员签字协议（强制阅读 + 内容） -->
    <el-card style="margin-top:16px">
      <template slot="header">
        <div class="card-header">
          <span>小程序签字协议</span>
          <span style="font-size:12px;color:#909399">业务员签字弹窗里展示的协议内容 + 强制阅读秒数</span>
        </div>
      </template>
      <el-form label-width="120px" style="max-width:800px">
        <el-form-item label="强制阅读秒数">
          <el-input-number v-model="signAgreementSeconds" :min="0" :max="120" :step="1" controls-position="right"></el-input-number>
          <span class="logo-tip" style="margin-left:10px">业务员点开协议后倒计时，归零才能点「确认同意」。填 0 跳过强制阅读。</span>
        </el-form-item>
        <el-form-item label="协议内容">
          <el-input v-model="signAgreementText" type="textarea" :rows="14" maxlength="5000" show-word-limit placeholder="请输入协议正文，支持换行"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="saveSignAgreement" :loading="signAgreementSaving">保存协议</el-button>
          <el-button @click="resetSignAgreement">恢复默认</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 短信验证码配置（速码信息通/凌凯平台） -->
    <el-card style="margin-top:16px">
      <template slot="header">
        <div class="card-header">
          <div>
            <span><i class="el-icon-message"></i> 短信验证码配置</span>
            <span style="font-size:12px;color:#909399;margin-left:10px">接入凌凯/速码信息通「接口II平台」用于产品试用手机验证码核验</span>
          </div>
          <div>
            <el-button type="success" size="mini" icon="el-icon-coin" @click="checkSmsBalance" :loading="checkingSmsBalance">查询短信余额</el-button>
            <el-button type="warning" size="mini" icon="el-icon-mobile-phone" @click="openTestSmsDialog">发送测试短信</el-button>
          </div>
        </div>
      </template>
      <el-form :model="smsForm" label-width="170px" style="max-width:800px" autocomplete="off">
        <!-- 防浏览器密码管理器自动填充账号密码 -->
        <input type="text" style="position:fixed;top:-9999px;opacity:0" tabindex="-1" />
        <input type="password" style="position:fixed;top:-9999px;opacity:0" tabindex="-1" />

        <el-form-item label="企业账号 (CorpID)">
          <el-input v-model="smsForm.sms_corpid" name="sms_corpid_config_input" autocomplete="off" placeholder="请输入短信平台企业账号 CorpID"></el-input>
        </el-form-item>
        <el-form-item label="接口密码 (Pwd)">
          <el-input v-model="smsForm.sms_pwd" name="sms_pwd_config_input" show-password autocomplete="new-password" placeholder="请输入接口调用密码 Pwd"></el-input>
        </el-form-item>
        <el-form-item label="接口发送地址">
          <el-input v-model="smsForm.sms_api_url" placeholder="https://sdk2.028lk.com/sdk2/BatchSend2.aspx"></el-input>
          <div class="logo-tip" style="margin-top:4px">默认：https://sdk2.028lk.com/sdk2/BatchSend2.aspx（备用：https://sdk3.028lk.com:9988/BatchSend2.aspx）</div>
        </el-form-item>
        <el-form-item label="短信中文签名">
          <el-input v-model="smsForm.sms_sign" placeholder="请输入短信中文签名"></el-input>
          <div class="logo-tip" style="margin-top:4px">接口II平台规范：短信内容末尾必须带企业中文签名</div>
        </el-form-item>
        <el-form-item label="验证码有效期 (分钟)">
          <el-input-number v-model="smsForm.sms_expire_minutes" :min="1" :max="60" :step="1" controls-position="right"></el-input-number>
          <div class="logo-tip" style="margin-top:4px">短信验证码的有效时间，默认 5 分钟</div>
        </el-form-item>
        <!-- 短信内容模版（内置标准模版，后台已隐藏）
        <el-form-item label="短信内容模版">
          <el-input v-model="smsForm.sms_template" type="textarea" :rows="3" placeholder="您正在申请产品试用，验证码为：{code}，{minutes}分钟内有效，请勿向他人泄露。"></el-input>
          <div class="logo-tip" style="margin-top:4px">模版格式需与短信平台报备模版完全一致。支持变量：{code} 验证码，{minutes} 分钟数</div>
        </el-form-item>
        <el-form-item label="短信效果预览">
          <div style="background:#f4f4f5;padding:10px 14px;border-radius:4px;color:#606266;font-size:13px;line-height:1.6">
            {{ previewSmsContent }}
          </div>
        </el-form-item>
        -->
        <el-form-item>
          <el-button type="primary" @click="saveSmsConfig" :loading="savingSms">保存短信配置</el-button>
          <el-button @click="resetSmsConfig">恢复默认</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 发送测试短信弹窗 -->
    <el-dialog title="发送测试短信" :visible.sync="testSmsDialogVisible" width="460px" :close-on-click-modal="false">
      <el-form label-width="100px">
        <el-form-item label="接收手机号">
          <el-input v-model="testSmsPhone" placeholder="请输入接收测试短信的手机号" maxlength="11"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="testSmsDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="sendingTestSms" @click="doSendTestSms">立即发送</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { configApi, fileApi, smsApi } from '../api/api'

export default {
  data() {
    return {
      form: { system_name: '', system_logo: '' },
      banners: [],
      saving: false,
      theme: { primary: '#2793FF', sidebar: '#2793FF', compact: false },
      themeSaving: false,
      maxUnsignedLimit: 3,
      maxNewoutUnfinishedLimit: 3,
      maxCustomerDebtLimit: 3,
      maxCustomerYearAmount: 100000,
      serviceFeeBufferValue: 7,
      serviceFeeBufferUnit: 'day',
      vipABufferMonths: 12,
      vipADormantMonths: 12,
      giftDormantMonths: null,
      businessRulesSaving: false,
      // 业务员列表和解禁选择
      salesmen: [],
      selectedUnsignedUser: null,
      selectedNewoutUser: null,
      selectedOverdueUser: null,
      signAgreementText: '',
      signAgreementSeconds: 8,
      signAgreementSaving: false,
      // 短信验证码配置（支持模版和有效期配置）
      smsForm: {
        sms_corpid: '',
        sms_pwd: '',
        sms_api_url: 'https://sdk2.028lk.com/sdk2/BatchSend2.aspx',
        sms_sign: '',
        sms_expire_minutes: 5,
        sms_template: '您正在申请产品试用，验证码为：{code}，{minutes}分钟内有效，请勿向他人泄露。'
      },
      savingSms: false,
      checkingSmsBalance: false,
      testSmsDialogVisible: false,
      testSmsPhone: '',
      sendingTestSms: false,
      defaultSignAgreement: '一、服务内容\n服务方提供的产品出库服务，包括但不限于：\n1. 产品出库前的人工检查、款式核对、数量核对等\n2. 产品包装、贴标、装箱等\n3. 产品发货的物流安排、追踪、垫付等\n\n二、服务费用\n服务费用为：人民币 元/单，用于产品出库服务的人工费、材料费等。\n\n三、双方权利义务\n1. 服务方应按照约定的服务内容和标准提供服务\n2. 客户应提供真实、准确的产品信息和出库要求\n3. 客户应按照约定支付服务费用\n\n四、违约责任\n1. 服务方未按照约定提供服务的，应承担相应的违约责任\n2. 客户未按照约定支付服务费用的，应承担相应的违约责任\n\n五、争议解决\n本协议的签订、履行、解释及争议的解决均适用中华人民共和国法律。\n\n六、其他\n本协议自双方签字（盖章）之日起生效，有效期为 年。',
      presets: [
        { key: 'elder',     name: '老年企业版', desc: '蓝白稳重主色，字号加大，对比清晰', primary: '#2793FF', sidebar: '#2793FF', compact: false },
        { key: 'skyblue',   name: '清爽天蓝',   desc: '亮蓝主色 + 浅蓝灰侧栏',         primary: '#1976D2', sidebar: '#1E3A8A', compact: false },
        { key: 'default',   name: '默认蓝',     desc: '经典深色侧栏 + Element 蓝',     primary: '#409EFF', sidebar: '#001529', compact: false },
        { key: 'midnight',  name: '深邃黑',     desc: '纯黑侧栏，沉稳低干扰',           primary: '#177DDC', sidebar: '#0B0B0F', compact: false },
        { key: 'business',  name: '商务红',     desc: '稳重蓝灰底 + 中国红主色',        primary: '#D9363E', sidebar: '#2B2F36', compact: false },
        { key: 'fresh',     name: '清新绿',     desc: '墨绿侧栏 + 翠绿点缀',             primary: '#13C2C2', sidebar: '#1F2D3D', compact: false },
        { key: 'sunset',    name: '日暮橙',     desc: '蓝灰底 + 暖橙主色',                primary: '#FA8C16', sidebar: '#304156', compact: false },
        { key: 'eyecare',   name: '护眼灰',     desc: '中性灰侧栏，长时间使用不刺眼',   primary: '#52C41A', sidebar: '#3A3F4B', compact: false },
        { key: 'royal',     name: '皇家紫',     desc: '深紫底 + 玫紫主色，颇具质感',     primary: '#722ED1', sidebar: '#1B1432', compact: false },
        { key: 'compact',   name: '紧凑蓝',     desc: '蓝白主题 + 紧凑模式，适合小屏',  primary: '#2793FF', sidebar: '#2793FF', compact: true  }
      ]
    }
  },
  computed: {
    previewSmsContent() {
      const tpl = (this.smsForm.sms_template || '您正在申请产品试用，验证码为：{code}，{minutes}分钟内有效，请勿向他人泄露。').trim()
      const sign = this.smsForm.sms_sign ? `【${this.smsForm.sms_sign.replace(/【|】/g, '')}】` : '【XX】'
      const mins = this.smsForm.sms_expire_minutes || 5

      let txt = tpl.replace('{code}', '123456')
      txt = txt.replace('验证码为：XX', '验证码为：123456')
      txt = txt.replace('验证码为：xx', '验证码为：123456')

      txt = txt.replace('{minutes}', String(mins))
      txt = txt.replace('XX分钟内有效', mins + '分钟内有效')
      txt = txt.replace('xx分钟内有效', mins + '分钟内有效')

      if (txt.includes('{sign}')) {
        txt = txt.replace('{sign}', sign)
      } else if (txt.includes('【XX】') || txt.includes('【xx】') || txt.includes('【签名】')) {
        txt = txt.replace('【XX】', sign).replace('【xx】', sign).replace('【签名】', sign)
      } else if (!txt.endsWith(sign)) {
        if (/【[^】]+】$/.test(txt)) {
          txt = txt.replace(/【[^】]+】$/, sign)
        } else {
          txt += sign
        }
      }
      return txt
    }
  },
  mounted() {
    this.loadConfigs()
    this.loadSalesmen()
  },
  watch: {
    'theme.primary'() { this.previewTheme() },
    'theme.sidebar'() { this.previewTheme() },
    'theme.compact'() { this.previewTheme() }
  },
  methods: {
    loadConfigs() {
      configApi.getConfigs().then(res => {
        this.form.system_name = res.data.system_name || '存货宝'
        this.form.system_logo = res.data.system_logo || ''
        const raw = res.data.home_banners
        if (raw) {
          try {
            const arr = JSON.parse(raw)
            this.banners = Array.isArray(arr) ? arr : []
          } catch (e) {
            this.banners = String(raw).split(',').map(s => s.trim()).filter(Boolean)
          }
        } else {
          this.banners = []
        }
        this.theme.primary = res.data.theme_primary_color || '#2793FF'
        this.theme.sidebar = res.data.theme_sidebar_color || '#2793FF'
        this.theme.compact = res.data.theme_compact === '1' || res.data.theme_compact === 'true'
        const limit = parseInt(res.data.max_unsigned_limit, 10)
        this.maxUnsignedLimit = isNaN(limit) ? 3 : limit
        const newoutLimit = parseInt(res.data.max_newout_unfinished_limit, 10)
        this.maxNewoutUnfinishedLimit = isNaN(newoutLimit) ? 3 : newoutLimit
        const debtLimit = parseInt(res.data.max_customer_debt_limit, 10)
        this.maxCustomerDebtLimit = isNaN(debtLimit) ? 3 : debtLimit
        const yearAmount = parseInt(res.data.max_customer_year_amount, 10)
        this.maxCustomerYearAmount = isNaN(yearAmount) ? 100000 : yearAmount
        const bufferValue = parseInt(res.data.service_fee_dormant_buffer_value, 10)
        this.serviceFeeBufferValue = isNaN(bufferValue) ? 7 : bufferValue
        this.serviceFeeBufferUnit = res.data.service_fee_dormant_buffer_unit || 'day'
        const vbm = parseInt(res.data.vip_a_buffer_months, 10)
        this.vipABufferMonths = isNaN(vbm) ? 12 : vbm
        const vdm = parseInt(res.data.vip_a_dormant_months, 10)
        this.vipADormantMonths = isNaN(vdm) ? 12 : vdm
        const gdm = parseInt(res.data.gift_dormant_months, 10)
        this.giftDormantMonths = isNaN(gdm) ? null : gdm
        this.signAgreementText = res.data.sign_agreement_text || this.defaultSignAgreement
        const secs = parseInt(res.data.sign_agreement_seconds, 10)
        this.signAgreementSeconds = isNaN(secs) ? 8 : secs
        // 短信配置
        this.smsForm.sms_corpid = res.data.sms_corpid || ''
        this.smsForm.sms_pwd = res.data.sms_pwd || ''
        this.smsForm.sms_api_url = res.data.sms_api_url || 'https://sdk2.028lk.com/sdk2/BatchSend2.aspx'
        this.smsForm.sms_sign = res.data.sms_sign || ''
        const expMins = parseInt(res.data.sms_expire_minutes, 10)
        this.smsForm.sms_expire_minutes = isNaN(expMins) ? 5 : expMins
        this.smsForm.sms_template = res.data.sms_template || '您正在申请产品试用，验证码为：{code}，{minutes}分钟内有效，请勿向他人泄露。'
      })
    },
    saveSmsConfig() {
      this.savingSms = true
      configApi.saveConfigs({
        sms_corpid: this.smsForm.sms_corpid || '',
        sms_pwd: this.smsForm.sms_pwd || '',
        sms_api_url: this.smsForm.sms_api_url || 'https://sdk2.028lk.com/sdk2/BatchSend2.aspx',
        sms_sign: this.smsForm.sms_sign || '',
        sms_expire_minutes: String(this.smsForm.sms_expire_minutes || 5),
        sms_template: this.smsForm.sms_template || ''
      }).then(res => {
        if (res.data && res.data.success) {
          this.$message.success('短信配置已保存')
        } else {
          this.$message.error((res.data && res.data.message) || '保存失败')
        }
      }).finally(() => { this.savingSms = false })
    },
    resetSmsConfig() {
      this.smsForm = {
        sms_corpid: '',
        sms_pwd: '',
        sms_api_url: 'https://sdk2.028lk.com/sdk2/BatchSend2.aspx',
        sms_sign: '',
        sms_expire_minutes: 5,
        sms_template: '您正在申请产品试用，验证码为：{code}，{minutes}分钟内有效，请勿向他人泄露。'
      }
    },
    checkSmsBalance() {
      this.checkingSmsBalance = true
      smsApi.getBalance().then(res => {
        if (res.data && res.data.success) {
          this.$alert(`当前短信平台剩余可用条数：<strong style="color:#67C23A;font-size:18px">${res.data.balance}</strong> 条`, '短信余额查询结果', {
            dangerouslyUseHTMLString: true,
            type: 'success'
          })
        } else {
          this.$alert((res.data && res.data.message) || '查询失败，请检查企业账号和密码是否正确', '余额查询结果', {
            type: 'warning'
          })
        }
      }).catch(err => {
        this.$message.error('网络请求失败: ' + (err.message || err))
      }).finally(() => { this.checkingSmsBalance = false })
    },
    openTestSmsDialog() {
      this.testSmsPhone = ''
      this.testSmsDialogVisible = true
    },
    doSendTestSms() {
      if (!this.testSmsPhone || !/^1\d{10}$/.test(this.testSmsPhone)) {
        this.$message.warning('请输入正确的11位手机号码')
        return
      }
      this.sendingTestSms = true
      smsApi.testSend(this.testSmsPhone).then(res => {
        if (res.data && res.data.success) {
          this.$message.success(res.data.message || '测试短信发送成功')
          this.testSmsDialogVisible = false
        } else {
          this.$message.error(res.data.message || '测试短信发送失败')
        }
      }).catch(err => {
        this.$message.error('发送请求失败: ' + (err.message || err))
      }).finally(() => { this.sendingTestSms = false })
    },
    saveBusinessRules() {
      this.businessRulesSaving = true
      configApi.saveConfigs({
        max_unsigned_limit: String(this.maxUnsignedLimit || 3),
        max_newout_unfinished_limit: String(this.maxNewoutUnfinishedLimit || 3),
        max_customer_debt_limit: String(this.maxCustomerDebtLimit || 3),
        max_customer_year_amount: String(this.maxCustomerYearAmount || 100000),
        service_fee_dormant_buffer_value: String(this.serviceFeeBufferValue || 7),
        service_fee_dormant_buffer_unit: this.serviceFeeBufferUnit || 'day',
        vip_a_buffer_months: String(this.vipABufferMonths || 12),
        vip_a_dormant_months: String(this.vipADormantMonths || 12),
        gift_dormant_months: this.giftDormantMonths != null ? String(this.giftDormantMonths) : '0'
      }).then(res => {
        if (res.data && res.data.success) {
          this.$message.success('业务规则已保存，正在重新计算禁售名单和休眠状态...')
          // 保存成功后，自动重新计算所有客户的禁售状态
          this.$axios.post('/customer/fix-amount-ban').then(banRes => {
            if (banRes.data && banRes.data.success) {
              const unbanned = banRes.data.unbanned || []
              const newBanned = banRes.data.newBanned || []
              const messages = []
              if (newBanned.length > 0) {
                messages.push(`新增禁售 ${newBanned.length} 个客户`)
              }
              if (unbanned.length > 0) {
                messages.push(`解除禁售 ${unbanned.length} 个客户`)
              }
              if (messages.length > 0) {
                this.$message.success('禁售名单已更新：' + messages.join('，') + '；请刷新页面查看最新休眠状态')
              } else {
                this.$message.info('所有客户的禁售状态均正确，无需调整；请刷新页面查看最新休眠状态')
              }
            }
          }).catch(err => {
            console.error('重新计算禁售名单失败:', err)
            this.$message.error('重新计算禁售名单失败')
          })
        }
      }).finally(() => { this.businessRulesSaving = false })
    },
    resetBusinessRules() {
      this.maxUnsignedLimit = 3
      this.maxNewoutUnfinishedLimit = 3
      this.maxCustomerDebtLimit = 3
      this.maxCustomerYearAmount = 100000
      this.serviceFeeBufferValue = 7
      this.serviceFeeBufferUnit = 'day'
      this.vipABufferMonths = 12
      this.vipADormantMonths = 12
      this.giftDormantMonths = 0
    },
    saveSignAgreement() {
      this.signAgreementSaving = true
      configApi.saveConfigs({
        sign_agreement_text: this.signAgreementText || '',
        sign_agreement_seconds: String(this.signAgreementSeconds || 0)
      }).then(res => {
        if (res.data && res.data.success) this.$message.success('协议已保存（小程序需重新打开签字页）')
      }).finally(() => { this.signAgreementSaving = false })
    },
    resetSignAgreement() {
      this.signAgreementText = this.defaultSignAgreement
      this.signAgreementSeconds = 8
    },
    saveTheme() {
      this.themeSaving = true
      const payload = {
        theme_primary_color: this.theme.primary || '#2793FF',
        theme_sidebar_color: this.theme.sidebar || '#2793FF',
        theme_compact: this.theme.compact ? '1' : '0'
      }
      configApi.saveConfigs(payload).then(res => {
        if (res.data.success) {
          this.$message.success('风格已保存')
          this.$root.$emit('config-updated')
        }
      }).finally(() => { this.themeSaving = false })
    },
    resetTheme() {
      this.theme = { primary: '#2793FF', sidebar: '#2793FF', compact: false }
      this.previewTheme()
    },
    applyPreset(p) {
      this.theme = { primary: p.primary, sidebar: p.sidebar, compact: !!p.compact }
      this.previewTheme()
    },
    isActivePreset(p) {
      return this.theme.primary === p.primary &&
             this.theme.sidebar === p.sidebar &&
             !!this.theme.compact === !!p.compact
    },
    previewTheme() {
      const root = document.documentElement
      root.style.setProperty('--theme-primary', this.theme.primary)
      root.style.setProperty('--theme-sidebar-bg', this.theme.sidebar)
      document.body.classList.toggle('theme-compact', !!this.theme.compact)
    },
    async handleLogoUpload(file) {
      if (!file.raw) return
      try {
        const res = await fileApi.upload(file.raw)
        if (res.data.success) {
          this.form.system_logo = res.data.url
          this.$message.success('Logo上传成功')
        }
      } catch (e) {
        this.$message.error('上传失败')
      }
    },
    async handleBannerUpload(file) {
      if (!file.raw) return
      try {
        const res = await fileApi.upload(file.raw)
        if (res.data.success) {
          this.banners.push(res.data.url)
          this.$message.success('图片上传成功')
        }
      } catch (e) {
        this.$message.error('上传失败')
      }
    },
    removeBanner(idx) { this.banners.splice(idx, 1) },
    moveBanner(idx, delta) {
      const target = idx + delta
      if (target < 0 || target >= this.banners.length) return
      const arr = this.banners.slice()
      const [x] = arr.splice(idx, 1)
      arr.splice(target, 0, x)
      this.banners = arr
    },
    handleSave() {
      if (!this.form.system_name.trim()) {
        this.$message.error('系统名称不能为空')
        return
      }
      this.saving = true
      const payload = {
        ...this.form,
        home_banners: JSON.stringify(this.banners || [])
      }
      configApi.saveConfigs(payload).then(res => {
        if (res.data.success) {
          this.$message.success('保存成功，刷新页面后生效')
          // 通知App.vue更新
          this.$root.$emit('config-updated')
        }
      }).finally(() => { this.saving = false })
    },
    getImageUrl(url) {
      if (!url) return ''
      if (url.startsWith('http') || url.startsWith('data:')) return url
      return 'https://chb.bjzkbh.com' + url
    },
    loadSalesmen() {
      // 加载所有业务员（roleId=3）
      this.$axios.get('/user/list').then(res => {
        if (res.data && Array.isArray(res.data)) {
          this.salesmen = res.data.filter(u => u.roleId === 3)
        }
      })
    },
    unlockBonus(bonusType) {
      let userId = null
      let typeName = ''
      if (bonusType === 'unsigned') {
        userId = this.selectedUnsignedUser
        typeName = '未签字明细'
      } else if (bonusType === 'newout') {
        userId = this.selectedNewoutUser
        typeName = '新出库未完结订单'
      } else if (bonusType === 'overdue') {
        userId = this.selectedOverdueUser
        typeName = '逾期还款期数'
      }

      if (!userId) {
        this.$message.warning('请选择要解禁的业务员')
        return
      }

      this.$confirm('确认给该业务员解禁？解禁后将增加对应的配额。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$axios.post('/user/unlock-bonus', { userId, bonusType }).then(res => {
          if (res.data && res.data.success) {
            this.$message.success(res.data.message || '解禁成功')
            // 清空选择
            if (bonusType === 'unsigned') this.selectedUnsignedUser = null
            if (bonusType === 'newout') this.selectedNewoutUser = null
            if (bonusType === 'overdue') this.selectedOverdueUser = null
          } else {
            this.$message.error(res.data.message || '解禁失败')
          }
        }).catch(err => {
          this.$message.error((err.response && err.response.data && err.response.data.message) || err.message || '解禁失败')
        })
      })
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; align-items: center; justify-content: space-between; }
.logo-area { display: inline-block; }
.logo-preview {
  position: relative;
  width: 128px;
  height: 128px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
}
.logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.logo-actions {
  position: absolute;
  top: 4px;
  right: 4px;
}
.logo-uploader {
  width: 128px;
  height: 128px;
}
.logo-placeholder {
  width: 128px;
  height: 128px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  cursor: pointer;
  transition: border-color 0.3s;
}
.logo-placeholder:hover { border-color: #2793FF; color: #2793FF; }
.logo-placeholder i { font-size: 28px; margin-bottom: 6px; }
.logo-placeholder p { font-size: 12px; margin: 0; }
.logo-tip { font-size: 12px; color: #909399; margin-top: 8px; }
.preview-box { display: inline-block; }
.preview-sidebar {
  width: 220px;
  height: 60px;
  background: #2793FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-logo-area { display: flex; align-items: center; }
.preview-logo-img { width: 28px; height: 28px; object-fit: contain; margin-right: 8px; border-radius: 4px; background: #fff; padding: 2px; }
.preview-logo-icon { font-size: 28px; color: #fff; margin-right: 8px; }
.preview-logo-text { font-size: 18px; font-weight: 700; color: #fff; letter-spacing: 2px; }

/* 轮播图管理 */
.banner-list { display: flex; flex-wrap: wrap; gap: 12px; }
.banner-item {
  position: relative;
  width: 240px;
  height: 112px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
}
.banner-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.banner-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
}
.banner-uploader { width: 240px; height: 112px; }
.banner-placeholder {
  width: 240px;
  height: 112px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  cursor: pointer;
  transition: border-color 0.3s;
}
.banner-placeholder:hover { border-color: #2793FF; color: #2793FF; }
.banner-placeholder i { font-size: 28px; margin-bottom: 4px; }
.banner-placeholder p { font-size: 12px; margin: 0; }

/* 后台风格预设栅格 */
.preset-grid { margin-bottom: 8px; }
.preset-grid .el-col { margin-bottom: 16px; }
.preset-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s ease;
  position: relative;
}
.preset-card:hover {
  border-color: var(--theme-primary, #2793FF);
  box-shadow: 0 4px 14px rgba(0,0,0,0.08);
  transform: translateY(-2px);
}
.preset-card.active {
  border-color: var(--theme-primary, #2793FF);
  box-shadow: 0 0 0 2px rgba(39,147,255,0.18);
}
.preset-thumb {
  height: 64px;
  border-radius: 6px;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}
.preset-thumb-logo {
  width: 60%;
  height: 8px;
  margin: 0 auto 4px;
  background: rgba(255,255,255,0.5);
  border-radius: 2px;
}
.preset-thumb-item {
  height: 6px;
  margin: 0 12px;
  background: rgba(255,255,255,0.18);
  border-radius: 2px;
}
.preset-meta {
  display: flex;
  align-items: center;
  margin-top: 8px;
  font-size: 13px;
  color: #303133;
}
.preset-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;
}
.preset-name { font-weight: 600; flex: 1; }
.preset-check { color: #67C23A; font-size: 16px; }
.preset-desc {
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 30px;
}
</style>
